import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { User, Calendar, Loader2 } from "lucide-react";
import type { SessionsGridProps } from "./types";
import { formatCalendarDate } from "./utils";

export const SessionsGrid = ({
  sessions,
  selectedUser,
  selectedSession,
  loading,
  loadingMore,
  hasMore,
  onSessionClick,
  onLoadMore,
}: SessionsGridProps) => {
  return (
    <Card className="flex min-h-0 flex-col overflow-hidden min-w-0 border border-border/60 bg-white/80 shadow-sm">
      <CardHeader className="border-b p-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">
            {selectedUser ? `Sessions` : "Sessions"}
          </CardTitle>
          {selectedUser && sessions.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {sessions.length}
              {hasMore ? "+" : ""}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-y-auto p-5">
        {!selectedUser ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground text-center">
            <div className="size-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
              <User className="size-8 opacity-50" />
            </div>
            <p className="text-sm font-medium mb-1">No user selected</p>
            <p className="text-xs opacity-70">
              Select a user from the left panel to view their sessions
            </p>
          </div>
        ) : loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <div className="size-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin mb-3" />
            <p className="text-sm">Loading sessions...</p>
          </div>
        ) : sessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground text-center">
            <div className="size-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
              <Calendar className="size-8 opacity-50" />
            </div>
            <p className="text-sm font-medium mb-1">No sessions found</p>
            <p className="text-xs opacity-70">
              This user has no recordings in the selected date range
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3 w-full max-md:grid-cols-1">
            {sessions.map((session) => {
              const sortedDates = [...session.dates].sort();
              const latestDate = sortedDates.at(-1) || "";
              const earliestDate = sortedDates[0] || "";
              const isMultiDay = sortedDates.length > 1;
              return (
                <Card
                  key={session.session_id}
                  className={`cursor-pointer transition-colors ${
                    selectedSession?.session_id === session.session_id
                      ? "border-primary bg-accent"
                      : "hover:bg-muted/50"
                  }`}
                  onClick={() => onSessionClick(session)}
                >
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">
                          {formatCalendarDate(latestDate)}
                        </span>
                        {isMultiDay && (
                          <span className="text-[0.65rem] text-muted-foreground">
                            {formatCalendarDate(earliestDate)} –{" "}
                            {formatCalendarDate(latestDate)} ·{" "}
                            {sortedDates.length} days
                          </span>
                        )}
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {session.recording_count}
                      </Badge>
                    </div>
                    {session.lesson_name ? (
                      <div
                        className="text-sm font-medium truncate mb-1"
                        title={session.lesson_name}
                      >
                        {session.lesson_name}
                      </div>
                    ) : (
                      session.lesson_id && (
                        <div className="text-sm text-muted-foreground italic mb-1">
                          Lesson #{session.lesson_id}
                        </div>
                      )
                    )}
                    {session.lesson_id && session.lesson_name && (
                      <Badge variant="outline" className="text-xs font-mono mb-2">
                        Lesson #{session.lesson_id}
                      </Badge>
                    )}
                    {session.slide_index != null && (
                      <Badge variant="outline" className="text-xs font-mono mb-2">
                        Slide {session.slide_index}
                      </Badge>
                    )}
                    <div
                      className="text-xs text-muted-foreground font-mono truncate pt-2 border-t"
                      title={session.session_id}
                    >
                      {session.session_id.substring(0, 16)}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
        {!loading && hasMore && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={onLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? (
                <>
                  <Loader2 className="mr-1.5 size-3 animate-spin" />
                  Loading...
                </>
              ) : (
                "Load older sessions"
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
